import { useEffect, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { clearToken } from '../lib/auth'
import { useChannels, useMe } from '../lib/queries'
import ChannelSwitcher from './ChannelSwitcher'
import ConfirmDialog from './ui/ConfirmDialog'
import { Tooltip } from './ui/Tooltip'

interface Props {
  children: ReactNode
  // Aktif kanal ID — kanal-bağlamlı sayfalardan (ChannelPage, LinksPage, OverlayEdit) gelir.
  // Sağlanmazsa AppShell ilk kanalı default seçili gösterir.
  activeChannelId?: string | null
}

const SIDEBAR_COLLAPSED_KEY = 'appShell.sidebarCollapsed'

export default function AppShell({ children, activeChannelId = null }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const { data: user } = useMe()
  const { data: channels = [] } = useChannels()

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
    } catch {
      return false
    }
  })
  const [confirmLogout, setConfirmLogout] = useState(false)

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0')
    } catch {
      /* sessionStorage disabled — yut */
    }
  }, [collapsed])

  function logout() {
    clearToken()
    navigate('/login')
  }

  // Sidebar her zaman bir kanal göstersin: aktif yoksa ilk kanal fallback.
  const fallbackChannel = channels[0] || null
  const displayChannelId = activeChannelId || fallbackChannel?.id || null
  const displayChannel = channels.find((c) => c.id === displayChannelId) || null

  // Sub-link aktiflik kontrolü URL bazlı
  function chanNavActive(suffix: string) {
    if (!activeChannelId) return false
    const base = `/channels/${activeChannelId}`
    return suffix === '' ? location.pathname === base : location.pathname === base + suffix
  }

  const asideWidth = collapsed ? 'w-14' : 'w-60'

  return (
    <div className="flex h-screen bg-[var(--color-bg)]">
      <aside
        className={`${asideWidth} border-r border-[var(--color-border)] flex flex-col shrink-0 transition-[width] duration-200`}
      >
        {/* Logo + collapse toggle */}
        <div className="px-3 h-14 flex items-center justify-between border-b border-[var(--color-border)]">
          <Link to="/dashboard" className="flex items-center gap-2 min-w-0">
            <span className="w-6 h-6 rounded bg-gradient-to-br from-[var(--color-accent)] to-[#8a93f0] flex items-center justify-center text-white text-xs font-bold shrink-0">
              P
            </span>
            {!collapsed && <span className="font-semibold text-[var(--color-text)] truncate">pixelkit</span>}
          </Link>
          <Tooltip content={collapsed ? 'Kenar çubuğunu aç' : 'Kenar çubuğunu daralt'} side="right">
            <button
              onClick={() => setCollapsed((v) => !v)}
              className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-elevated)] shrink-0"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {collapsed ? <polyline points="9 18 15 12 9 6" /> : <polyline points="15 18 9 12 15 6" />}
              </svg>
            </button>
          </Tooltip>
        </div>

        {/* Channel switcher — collapsed mode'da gizle */}
        {!collapsed && (
          <div className="p-2 border-b border-[var(--color-border)]">
            <ChannelSwitcher channels={channels} activeChannelId={displayChannelId} />
          </div>
        )}

        {/* Channel-context nav */}
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {displayChannel && (
            <>
              {!collapsed && (
                <div className="px-2.5 pt-1 pb-1 text-[10px] uppercase tracking-wider text-[var(--color-text-subtle)]">
                  Kanal
                </div>
              )}
              <NavLink
                to={`/channels/${displayChannel.id}`}
                active={chanNavActive('') || chanNavActive('/edit')}
                collapsed={collapsed}
                icon={
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <line x1="9" y1="9" x2="15" y2="9" />
                    <line x1="9" y1="13" x2="15" y2="13" />
                    <line x1="9" y1="17" x2="13" y2="17" />
                  </svg>
                }
                label="Genel"
              />
              <NavLink
                to={`/channels/${displayChannel.id}/links`}
                active={chanNavActive('/links')}
                collapsed={collapsed}
                icon={
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                }
                label="Linkler"
              />
              <NavLink
                to={`/channels/${displayChannel.id}/settings`}
                active={chanNavActive('/settings')}
                collapsed={collapsed}
                icon={
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                }
                label="Ayarlar"
              />
            </>
          )}
        </nav>

        {/* Hesap section — alt, ayrı görünsün */}
        <div className="border-t border-[var(--color-border)] p-2 space-y-0.5">
          {!collapsed && (
            <div className="px-2.5 pt-1 pb-1 text-[10px] uppercase tracking-wider text-[var(--color-text-subtle)]">
              Hesap
            </div>
          )}
          <NavLink
            to="/settings"
            active={location.pathname.startsWith('/settings')}
            collapsed={collapsed}
            icon={
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            }
            label="Ayarlar"
          />
          {user && !collapsed && (
            <div className="px-2.5 py-1.5 text-xs text-[var(--color-text-subtle)] truncate">
              {user.email}
            </div>
          )}
          <Tooltip content="Çıkış" side="right" enabled={collapsed}>
            <button
              onClick={() => setConfirmLogout(true)}
              className={`w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded text-sm text-[var(--color-text-muted)] hover:bg-[var(--color-danger)]/10 hover:text-[var(--color-danger)] ${
                collapsed ? 'justify-center' : ''
              }`}
            >
              {/* lucide power — chevron benzeri logout iconundan farklı, sidebar toggle ile karışmaz */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                <line x1="12" y1="2" x2="12" y2="12" />
              </svg>
              {!collapsed && 'Çıkış'}
            </button>
          </Tooltip>
        </div>
      </aside>
      <main className="flex-1 overflow-auto min-w-0">{children}</main>

      <ConfirmDialog
        open={confirmLogout}
        onClose={() => setConfirmLogout(false)}
        onConfirm={logout}
        title="Çıkış yap?"
        description="Tekrar giriş yapana kadar dashboard'a erişemezsin."
        confirmLabel="Çıkış yap"
      />
    </div>
  )
}

function NavLink({
  to,
  active,
  icon,
  label,
  collapsed = false,
}: {
  to: string
  active: boolean
  icon: ReactNode
  label: string
  collapsed?: boolean
}) {
  return (
    <Tooltip content={label} side="right" enabled={collapsed}>
      <Link
        to={to}
        className={[
          'flex items-center gap-2.5 px-2.5 py-1.5 rounded text-sm',
          collapsed ? 'justify-center' : '',
          active
            ? 'bg-[var(--color-elevated)] text-[var(--color-text)]'
            : 'text-[var(--color-text-muted)] hover:bg-[var(--color-elevated)] hover:text-[var(--color-text)]',
        ].join(' ')}
      >
        <span className="text-[var(--color-text-subtle)]">{icon}</span>
        {!collapsed && label}
      </Link>
    </Tooltip>
  )
}
